// Merge rules for the accumulating image list. Kept in its own module so they
// stay pure and testable — importing the hook drags in aws-amplify.

interface MergeableImage {
    id: string;
    name: string;
    upload_status: string;
    tile_status: string;
    updated_at: string;
}

const sameRow = (a: MergeableImage, b: MergeableImage) =>
    a.id === b.id &&
    a.name === b.name &&
    a.upload_status === b.upload_status &&
    a.tile_status === b.tile_status &&
    a.updated_at === b.updated_at;

// Page 1 is authoritative for its own slice (server orders newest first), so a
// refreshed page 1 replaces the head of the list rather than appending — new
// uploads show up at the top and existing rows pick up status changes.
// Returning prev unchanged matters: it lets React bail out on no-op polls.
//
// isWholeList = the server sent no next cursor. In practice the API only does
// that for an empty page, so this is narrowly the "everything is gone" case —
// and it is the case the prefix check below gets wrong on its own, because
// [].every() is vacuously true, so an emptied list would keep rendering its old
// rows forever. Rows deleted while other pages still exist are NOT handled here
// (page 1 cannot prove a missing row was deleted rather than paged out); those
// still rely on the hard refresh via bumpImageListVersion.
export const mergeFirstPage = <T extends MergeableImage>(
    prev: T[],
    page: T[],
    isWholeList = false
): T[] => {
    const lengthUnchanged = isWholeList ? page.length === prev.length : page.length <= prev.length;
    if (lengthUnchanged && page.every((img, i) => sameRow(img, prev[i]))) return prev;
    if (isWholeList) return page;
    const pageIds = new Set(page.map((img) => img.id));
    return [...page, ...prev.filter((img) => !pageIds.has(img.id))];
};

// ponytail: later pages only append — page 1 carries the status refreshes.
export const appendNew = <T extends MergeableImage>(prev: T[], page: T[]): T[] => {
    const seen = new Set(prev.map((img) => img.id));
    const fresh = page.filter((img) => !seen.has(img.id));
    return fresh.length ? [...prev, ...fresh] : prev;
};
